import React from 'react';
import { useDataProvider, useRefresh } from 'react-admin';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';

import { Dialog } from 'components/common';
import { Input } from 'components/form';
import { AchTierConfigDto } from '../types';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  input: {
    '& .MuiInput-input': {
      padding: theme.spacing(1, 0),
    },
  },
}));

const formatPriceInput = (value: string) =>
  value
    .replace(/[^\d.]/g, '')
    .replace(/\.(?=.*\.)/g, '')
    .replace(/^(\d+\.\d{0,2})\d*$/, '$1');

interface Props {
  record: AchTierConfigDto;
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  instantAvailabilityCapCents: number;
  minSuccessfulClearancesToPromote: number;
}

const EditTierDialog = ({ record, open, onClose }: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const handleSubmitForm = async (values: FormValues) => {
    try {
      const { data } = await dataProvider.update('achTiers', {
        data: {
          tier: record.tier,
          instantAvailabilityCapCents: values.instantAvailabilityCapCents * 100,
          minSuccessfulClearancesToPromote: values.minSuccessfulClearancesToPromote,
        },
      });

      refresh();
      onClose();

      return data;
    } catch (error: any) {
      return { [FORM_ERROR]: error.message ?? error };
    }
  };

  return (
    <Form<FormValues>
      initialValues={{
        instantAvailabilityCapCents: record.instantAvailabilityCapCents / 100,
        minSuccessfulClearancesToPromote: record.minSuccessfulClearancesToPromote,
      }}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitting, submitError }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Dialog
              open={open}
              onCancel={onClose}
              title="Edit tier"
              onConfirm={handleSubmit}
              confirmText="Save"
              submitting={submitting}
              error={submitError}>
              <Input
                name="instantAvailabilityCapCents"
                label="Instant availability cap"
                classes={{ input: classes.input }}
                inputProps={{
                  InputProps: {
                    startAdornment: <span>$</span>,
                  },
                }}
                formatValue={formatPriceInput}
              />
              <Input name="minSuccessfulClearancesToPromote" label="Min successful clearances" />
            </Dialog>
          </form>
        );
      }}
    />
  );
};

export default EditTierDialog;
